import {
  addDays,
  addMinutes,
  endOfDay,
  isBefore,
  set,
  startOfDay,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

export const coordPattern = /!2d(-?\d+\.\d+)!3d(-?\d+\.\d+)/;

interface Location {
  googleMapsEmbed: string;
}

interface Viewport {
  center: {
    lat: number;
    lng: number;
  };
  zoom: number;
}

export function calculateViewport(locations: Location[]): Viewport | undefined {
  if (!locations || locations.length === 0) {
    return undefined;
  }

  const coordinates = locations
    .map((location) => {
      const match = location.googleMapsEmbed?.match(coordPattern);
      if (!match) return null;

      const [, longitude, latitude] = match;
      return {
        lat: Number.parseFloat(latitude),
        lng: Number.parseFloat(longitude),
      };
    })
    .filter((coord): coord is { lat: number; lng: number } => coord !== null);

  if (coordinates.length === 0) {
    return undefined;
  }

  // Calculate the bounds
  const bounds = coordinates.reduce(
    (acc, coord) => ({
      north: Math.max(acc.north, coord.lat),
      south: Math.min(acc.south, coord.lat),
      east: Math.max(acc.east, coord.lng),
      west: Math.min(acc.west, coord.lng),
    }),
    {
      north: -90,
      south: 90,
      east: -180,
      west: 180,
    },
  );

  // Calculate center point
  const center = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };

  // Calculate appropriate zoom level
  const latDiff = bounds.north - bounds.south;
  const lngDiff = bounds.east - bounds.west;
  const maxDiff = Math.max(latDiff, lngDiff);

  // Rough zoom calculation - adjust these values as needed
  let zoom = 18; // Default zoom
  if (maxDiff > 0.1) zoom = 11;
  if (maxDiff > 0.5) zoom = 9;
  if (maxDiff > 1) zoom = 7;

  return {
    center,
    zoom,
  };
}

/**
 * Gets the start of "today" in the specified timezone.
 * This ensures that "today" is calculated based on the practice timezone,
 * not the browser's local timezone, preventing day shifts.
 */
export function getStartOfTodayInTimezone(timezone: string): Date {
  const now = new Date();
  // Convert current time to the practice timezone
  const zonedNow = toZonedTime(now, timezone);
  // Get start of day in the practice timezone
  const startOfDayInTz = startOfDay(zonedNow);
  // Convert back to UTC Date object
  return fromZonedTime(startOfDayInTz, timezone);
}

/**
 * Default number of days out that the earliest bookable appointment is,
 * for Athena products / HRTs that don't override it. Historically this was
 * hardcoded as `addDays(..., 2)` throughout the schedule flow.
 */
export const DEFAULT_BOOKING_LEAD_TIME_DAYS = 2;

/**
 * Minimum time between "now" and a bookable slot. Prevents same-day slots
 * from being offered so close to now that checkout (payment, EHR booking)
 * can't realistically finish before the slot is gone.
 */
export const MINIMUM_BOOKING_BUFFER_MINUTES = 120;

type HrtBookingLeadTime = {
  displayMetadata?: {
    overrideBookingTimeDays?: number | null;
  } | null;
};

/**
 * Determines how many days out the earliest bookable appointment is for the
 * given HRTs. Defaults to {@link DEFAULT_BOOKING_LEAD_TIME_DAYS} (2), but an
 * HRT can shorten (or lengthen) this via its
 * `displayMetadata.overrideBookingTimeDays` (e.g. 1 => bookable 1 day out).
 *
 * When multiple HRTs are booked together, the most restrictive (largest) lead
 * time wins so every HRT's requirement is satisfied. HRTs without a valid
 * override contribute the default lead time.
 */
export function getBookingLeadTimeDays(hrts: HrtBookingLeadTime[]): number {
  if (hrts.length === 0) {
    return DEFAULT_BOOKING_LEAD_TIME_DAYS;
  }

  const leadTimes = hrts.map((hrt) => {
    const override = hrt.displayMetadata?.overrideBookingTimeDays;
    return typeof override === "number" &&
      Number.isInteger(override) &&
      override >= 0
      ? override
      : DEFAULT_BOOKING_LEAD_TIME_DAYS;
  });

  return Math.max(...leadTimes);
}

/**
 * The `scheduleScreen` bag on an HRT's `displayMetadata`:
 *
 *   "scheduleScreen": {
 *     "text": "This provider does not always schedule for this treatment.",
 *     "color": "red", "size": "lg", "weight": "semibold"
 *   }
 *
 * Typed loosely on purpose — `displayMetadata` arrives as untyped GraphQL JSON
 * and is never schema-validated, so the reader below treats every field as
 * untrusted rather than assuming the Zod shape in `procedureSlice.ts` held.
 */
type HrtScheduleScreen = {
  displayMetadata?: {
    scheduleScreen?: {
      text?: string | null;
      color?: string | null;
      size?: string | null;
      weight?: string | null;
    } | null;
  } | null;
};

/**
 * Style tokens ops can set on an HRT to restyle its schedule-screen caveat.
 *
 * These are deliberately named tokens mapped to literal Tailwind classes here
 * rather than raw class strings pulled from config (the approach
 * `BaseComponent.style` + the `tailwind.config.ts` safelist take for CMS
 * components). Two reasons: the classes below are literal source text, so
 * Tailwind's JIT compiles them without a safelist entry that someone has to
 * remember to add; and ops can't accidentally break the order-summary rail
 * with a stray layout class. An unrecognized token falls back to the default
 * instead of rendering unstyled — lookup is trimmed and case-insensitive so
 * "Red" and "semiBold" work too.
 */
const SCHEDULE_TEXT_COLORS: Record<string, string> = {
  gray: "text-gray-500",
  black: "text-black",
  red: "text-red-600",
};

const SCHEDULE_TEXT_SIZES: Record<string, string> = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
};

const SCHEDULE_TEXT_WEIGHTS: Record<string, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

/** Matches the copy's previous hardcoded look, so unstyled HRTs don't move. */
const DEFAULT_SCHEDULE_TEXT_COLOR = SCHEDULE_TEXT_COLORS.gray;
const DEFAULT_SCHEDULE_TEXT_SIZE = SCHEDULE_TEXT_SIZES.xs;

export type ScheduleScreenText = {
  /** The configured copy, trimmed. */
  text: string;
  /** Tailwind classes resolved from this HRT's style tokens. */
  className: string;
};

function resolveScheduleTextToken(
  value: string | null | undefined,
  tokens: Record<string, string>,
  fallback: string,
): string {
  if (typeof value !== "string") {
    return fallback;
  }
  return tokens[value.trim().toLowerCase()] ?? fallback;
}

/**
 * Collects the schedule-screen caveats configured on the given HRTs via
 * `displayMetadata.scheduleScreen.text` (e.g. "This provider does not always
 * schedule for this treatment."), for rendering under the schedule screen's
 * "Continue without scheduling" CTA.
 *
 * Each entry carries the classes resolved from that HRT's own
 * `scheduleScreen.color` / `.size` / `.weight` tokens, so two HRTs in one cart
 * can be styled differently. Unset tokens keep the copy's original look
 * (`text-xs` gray, inherited weight).
 *
 * Intentionally NOT localized: this reads the single base value and ignores
 * `HrtTranslation.displayMetadata`, so whatever string ops configure is shown
 * verbatim to every visitor regardless of locale. Non-English storefronts will
 * see English copy here. If this ever needs translating, read it through
 * `getTranslatedHrtDisplayMetadata` (the way `description`/`ingredients` do)
 * rather than adding a next-intl key — the copy is org config, not bundled
 * product copy. Note the DB `LanguageCode` enum has no `fr`/`ht`, so those two
 * storefront locales could not be covered that way anyway.
 *
 * Unlike `showContinueWithoutSchedulingButton`, this looks at every HRT in the
 * cart rather than just the first, so a caveat on a bundle's second component
 * still surfaces. Blank values are dropped and duplicates collapsed, so
 * booking two HRTs that share the same caveat shows it once — the first
 * occurrence's styling wins.
 */
export function getScheduleScreenTexts(
  hrts: Array<HrtScheduleScreen | null | undefined>,
): ScheduleScreenText[] {
  const seen = new Set<string>();
  const texts: ScheduleScreenText[] = [];

  for (const hrt of hrts) {
    // Nullish entries are tolerated because the call site flattens
    // `cartItems.flatMap((item) => item.hrts)`, and `item.hrts` is itself
    // optional-chained elsewhere in OrderSummary — so a cart item without an
    // hrts array would otherwise put `undefined` in here and throw on the
    // property read below, taking the whole order-summary rail down.
    const scheduleScreen = hrt?.displayMetadata?.scheduleScreen;
    const rawText = scheduleScreen?.text;
    const text = typeof rawText === "string" ? rawText.trim() : "";
    if (!text || seen.has(text)) {
      continue;
    }
    seen.add(text);

    const className = [
      resolveScheduleTextToken(
        scheduleScreen?.size,
        SCHEDULE_TEXT_SIZES,
        DEFAULT_SCHEDULE_TEXT_SIZE,
      ),
      resolveScheduleTextToken(
        scheduleScreen?.color,
        SCHEDULE_TEXT_COLORS,
        DEFAULT_SCHEDULE_TEXT_COLOR,
      ),
      // No default weight — inherit, matching how this copy rendered before.
      resolveScheduleTextToken(
        scheduleScreen?.weight,
        SCHEDULE_TEXT_WEIGHTS,
        "",
      ),
    ]
      .filter(Boolean)
      .join(" ");

    texts.push({ text, className });
  }

  return texts;
}

export function getNext5Days(startDate: Date): Date[] {
  const dates = [] as Date[];
  for (let i = 0; i < 5; i++) {
    const currentDate = addDays(startDate, i);
    dates.push(i === 4 ? endOfDay(currentDate) : currentDate);
  }
  return dates;
}

// Generates an array of time slots between start and end times.
export const generateSlots = (
  start: Date,
  end: Date,
  minutesPerSlot: number,
): Date[] => {
  const slots: Date[] = [];

  let current = new Date(start);

  // Generate slots until we reach the end time
  while (isBefore(current, end)) {
    slots.push(new Date(current));
    current = addMinutes(current, minutesPerSlot);
  }

  return slots;
};

/**
 * Converts a time string to a Date object in the specified timezone.
 * Handles both ISO format ("1970-01-01T09:00:00.000Z") and HH:mm format.
 *
 * The result is a Date object representing the exact moment when that
 * wall-clock time occurs in the practice timezone on the given date.
 */
export const timeStringToDate = (
  date: Date,
  timeStr: string,
  timezone: string,
): Date => {
  if (!timeStr) {
    throw new Error(`Invalid time string: ${timeStr}`);
  }

  // Extract hours and minutes from the time string
  const { hours, minutes } = extractHoursMinutes(timeStr);

  // Validate the extracted time values
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    throw new Error(`Invalid time values: ${timeStr}`);
  }

  // Step 1: Convert the target date to the practice timezone
  // This gives us the calendar day as it appears in that timezone
  const zonedDate = toZonedTime(date, timezone);

  // Step 2: Set the wall-clock time (hours/minutes) on that date
  const withTime = set(zonedDate, {
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  });

  // Step 3: Convert back to a UTC Date object
  // This represents the absolute moment in time when that wall-clock time occurs
  return fromZonedTime(withTime, timezone);
};

/**
 * Extracts hours and minutes from a time string.
 * Supports two formats:
 * - ISO format: "1970-01-01T09:00:00.000Z" (extracts "09:00")
 * - Simple format: "09:00" or "9:00"
 */
export const extractHoursMinutes = (
  timeStr: string,
): { hours: number; minutes: number } => {
  // Handle ISO format: "1970-01-01T09:00:00.000Z"
  if (timeStr.includes("T")) {
    const timePart = timeStr.split("T")[1]; // "09:00:00.000Z"
    const [h, m] = timePart.split(":");
    return {
      hours: Number.parseInt(h, 10),
      minutes: Number.parseInt(m, 10),
    };
  }

  // Handle simple HH:mm format
  const [h, m] = timeStr.split(":");
  return {
    hours: Number.parseInt(h, 10),
    minutes: Number.parseInt(m, 10),
  };
};
